import { Link } from 'react-router-dom'
import { ArrowLeft, Search, Book, MessageCircle, Phone, Mail, FileText, Users, CreditCard, Shield, Zap, AlertCircle, CheckCircle, Clock } from 'lucide-react'
import { motion } from 'framer-motion'
import { useState } from 'react'

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, delay: 0.2 }
    }
  }

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, delay: 0.4 }
    }
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  }

  const categories = [
    { id: 'all', name: 'All Categories', icon: Book },
    { id: 'payments', name: 'Payments', icon: CreditCard },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'integration', name: 'Integration', icon: Zap },
    { id: 'account', name: 'Account', icon: Users },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: AlertCircle }
  ]

  const articles = [
    {
      id: 1,
      title: 'Getting Started with PexiBlock Payments',
      excerpt: 'Learn how to set up your first card-to-crypto payment integration in minutes.',
      category: 'payments',
      readTime: '5 min',
      isPopular: true
    },
    {
      id: 2,
      title: 'API Authentication & Security Best Practices',
      excerpt: 'Comprehensive guide to securing your API keys and implementing proper authentication.',
      category: 'security',
      readTime: '8 min',
      isPopular: true
    },
    {
      id: 3,
      title: 'Webhook Implementation Guide',
      excerpt: 'Set up real-time notifications for payment events in your application.',
      category: 'integration',
      readTime: '10 min',
      isPopular: false
    },
    {
      id: 4,
      title: 'Handling Payment Errors & Edge Cases',
      excerpt: 'Best practices for error handling and creating robust payment flows.',
      category: 'troubleshooting',
      readTime: '7 min',
      isPopular: true
    },
    {
      id: 5,
      title: 'Account Verification Process',
      excerpt: 'Step-by-step guide to verify your merchant account and increase limits.',
      category: 'account',
      readTime: '6 min',
      isPopular: false
    },
    {
      id: 6,
      title: 'Supported Cryptocurrencies & Networks',
      excerpt: 'Complete list of supported digital assets and blockchain networks.',
      category: 'payments',
      readTime: '4 min',
      isPopular: true
    },
    {
      id: 7,
      title: 'Rate Limiting & API Quotas',
      excerpt: 'Understanding API rate limits and how to optimize your integration.',
      category: 'integration',
      readTime: '5 min',
      isPopular: false
    },
    {
      id: 8,
      title: 'PCI DSS Compliance Guidelines',
      excerpt: 'How PexiBlock maintains PCI DSS compliance and protects cardholder data.',
      category: 'security',
      readTime: '12 min',
      isPopular: false
    },
    {
      id: 9,
      title: 'Transaction Monitoring & Reporting',
      excerpt: 'Access detailed transaction reports and monitor payment performance.',
      category: 'account',
      readTime: '6 min',
      isPopular: false
    },
    {
      id: 10,
      title: 'Common Integration Issues & Solutions',
      excerpt: 'Quick fixes for the most common integration problems developers face.',
      category: 'troubleshooting',
      readTime: '9 min',
      isPopular: true
    }
  ]

  const filteredArticles = articles.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  return (
    <motion.div 
      className="min-h-screen bg-gray-900"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Back Navigation */}
        <motion.div className="mb-8" variants={fadeInUp}>
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-orange-400 transition-colors hover:text-orange-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div className="mb-12 text-center" variants={fadeInUp}>
          <div className="flex items-center justify-center mb-4">
            <Book className="w-8 h-8 text-orange-400 mr-3" />
            <h1 className="text-4xl font-bold text-white">Help Center</h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Find answers, guides, and resources to help you integrate and optimize PexiBlock payments
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div className="mb-12 max-w-2xl mx-auto" variants={fadeInUp}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </motion.div>

        {/* Quick Access Cards */}
        <motion.div className="mb-16" variants={fadeInUp}>
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Quick Access</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Link to="/contact" className="group">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 text-center hover:bg-slate-800 transition-colors">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500/30 transition-colors">
                  <MessageCircle className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-semibold text-white mb-2 group-hover:text-orange-400 transition-colors">Contact Support</h3>
                <p className="text-gray-400 text-sm">Get help from our expert support team</p>
              </div>
            </Link>

            <Link to="/faq" className="group">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 text-center hover:bg-slate-800 transition-colors">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-green-500/30 transition-colors">
                  <FileText className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="font-semibold text-white mb-2 group-hover:text-orange-400 transition-colors">FAQ</h3>
                <p className="text-gray-400 text-sm">Frequently asked questions and answers</p>
              </div>
            </Link>

            <Link to="/developer-community" className="group">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 text-center hover:bg-slate-800 transition-colors">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-500/30 transition-colors">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="font-semibold text-white mb-2 group-hover:text-orange-400 transition-colors">Developer Community</h3>
                <p className="text-gray-400 text-sm">Join our developer community and resources</p>
              </div>
            </Link>

            <div className="group cursor-pointer">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 text-center hover:bg-slate-800 transition-colors">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-500/30 transition-colors">
                  <Phone className="w-6 h-6 text-orange-400" />
                </div>
                <h3 className="font-semibold text-white mb-2 group-hover:text-orange-400 transition-colors">Call Us</h3>
                <p className="text-gray-400 text-sm">Speak directly with our support team</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Category Filter */}
        <motion.div className="mb-8" variants={fadeInLeft}>
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => {
              const Icon = category.icon
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{category.name}</span>
                </button>
              )
            })}
          </div>
        </motion.div>

        {/* Articles Grid */}
        <motion.div variants={fadeInRight}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">
              {selectedCategory === 'all' ? 'All Articles' : `${categories.find(c => c.id === selectedCategory)?.name} Articles`}
            </h2>
            <p className="text-gray-400">{filteredArticles.length} articles found</p>
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredArticles.map((article) => (
                <motion.div
                  key={article.id}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:bg-slate-800 transition-colors group cursor-pointer"
                  variants={fadeInUp}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      article.category === 'payments' ? 'bg-blue-500/20 text-blue-400' :
                      article.category === 'security' ? 'bg-green-500/20 text-green-400' :
                      article.category === 'integration' ? 'bg-purple-500/20 text-purple-400' :
                      article.category === 'account' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {categories.find(c => c.id === article.category)?.name}
                    </span>
                    {article.isPopular && (
                      <div className="flex items-center space-x-1 text-orange-400">
                        <CheckCircle className="w-3 h-3" />
                        <span className="text-xs">Popular</span>
                      </div>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-white mb-2 group-hover:text-orange-400 transition-colors">
                    {article.title}
                  </h3>
                  
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{article.readTime} read</span>
                    </div>
                    <span className="text-orange-400 group-hover:text-orange-300">Read more →</span>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div 
              className="text-center py-12"
              variants={fadeInUp}
            >
              <AlertCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No articles found</h3>
              <p className="text-gray-400 mb-6">Try adjusting your search or category filter</p>
              <button
                onClick={() => {
                  setSearchQuery('')
                  setSelectedCategory('all')
                }}
                className="px-6 py-3 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
              >
                Clear Filters
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* Contact Section */}
        <motion.div 
          className="mt-16 p-8 bg-gradient-to-r from-orange-500/10 to-blue-500/10 rounded-2xl border border-orange-500/20"
          variants={fadeInUp}
        >
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Still Need Help?</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Can't find what you're looking for? Our support team is here to help you succeed with PexiBlock.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
              >
                Contact Support
              </Link>
              <a
                href="mailto:support@pexiblock.com"
                className="px-8 py-3 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Mail className="w-4 h-4" />
                <span>Email Us</span>
              </a>
            </div>
          </div>
        </motion.div>

        {/* Popular Resources */}
        <motion.div className="mt-16" variants={fadeInUp}>
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Popular Resources</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
              <h3 className="font-medium text-white mb-2">API Documentation</h3>
              <p className="text-gray-400 text-sm mb-3">Complete API reference and examples</p>
              <a href="#" className="text-orange-400 text-sm hover:text-orange-300">Learn more →</a>
            </div>
            <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
              <h3 className="font-medium text-white mb-2">SDK Libraries</h3>
              <p className="text-gray-400 text-sm mb-3">Official libraries for popular languages</p>
              <a href="#" className="text-orange-400 text-sm hover:text-orange-300">Download →</a>
            </div>
            <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
              <h3 className="font-medium text-white mb-2">Status Page</h3>
              <p className="text-gray-400 text-sm mb-3">Real-time system status and uptime</p>
              <a href="#" className="text-orange-400 text-sm hover:text-orange-300">Check status →</a>
            </div>
            <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
              <h3 className="font-medium text-white mb-2">Changelog</h3>
              <p className="text-gray-400 text-sm mb-3">Latest updates and feature releases</p>
              <a href="#" className="text-orange-400 text-sm hover:text-orange-300">View changes →</a>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}